import './Invoices.scss';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../commons/Auth';
import { useAlert } from 'react-alert';
import {
  useQueryParams,
  StringParam,
  withDefault,
  NumberParam,
} from 'use-query-params';
import { useLoading } from '../../commons/Loading';
import InvoicesPresentational from './InvoicesPresentational';
import invoicesAPIs from '../../lib/fetch/invoicesAPI';

function Invoices({ profile }) {
  const [invoices, setInvoices] = useState([]);
  const [condos, setCondos] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [utilizer, setUtilizer] = useState({});

  const [{ newToken } = {}] = useAuth();
  const [t] = useTranslation();
  const { showLoading, hideLoading } = useLoading();
  const alert = useAlert();

  const [query, setQuery] = useQueryParams({
    all: withDefault(StringParam, 'false'),
    admin: withDefault(StringParam, ''),
    customer: withDefault(StringParam, ''),
    number: withDefault(StringParam, ''),
    supplier: withDefault(StringParam, ''),
    year: withDefault(NumberParam, new Date().getFullYear()),
    accountabilityYear: NumberParam,
    accountabilityMonth: NumberParam,
    toBill: withDefault(StringParam, ''),
  });

  const {
    all,
    admin,
    customer,
    number,
    supplier,
    year,
    accountabilityMonth,
    accountabilityYear,
    toBill,
  } = query;

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    all,
    admin,
    customer,
    number,
    supplier,
    toBill,
    year,
    accountabilityMonth,
    accountabilityYear,
  ]);

  const fetchInvoices = async () => {
    showLoading();

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/invoices/${profile._id}`,
      {
        headers: { Authorization: newToken },
        params: {
          ...query,
          forSupplier: ['SUPPLIER', 'COMMERCIAL'].includes(profile.type),
        },
      }
    );

    if (response instanceof Error) {
      hideLoading();
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    setInvoices(response.data);
    hideLoading();
  };

  const searchAdministrators = async (value) => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/profiles/admins`,
      {
        headers: { Authorization: newToken },
        params: { search: value },
      }
    );

    if (response instanceof Error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      return [];
    }

    const { data = [] } = response;
    return data;
  };

  const searchEstateAgents = async (value) => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/profiles/estateAgents`,
      {
        headers: { Authorization: newToken },
        params: { search: value },
      }
    );

    if (response instanceof Error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      return [];
    }

    const { data = [] } = response;
    return data;
  };

  const searchAssociations = async (value) => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/profiles/associations`,
      {
        headers: { Authorization: newToken },
        params: { search: value },
      }
    );

    if (response instanceof Error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      return [];
    }

    const { data = [] } = response;
    return data;
  };

  const searchCommercials = async (value) => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/profiles/commercials`,
      {
        headers: { Authorization: newToken },
        params: { search: value },
      }
    );

    if (response instanceof Error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      return [];
    }

    const { data = [] } = response;
    return data;
  };

  const searchUtilizers = async (value) => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/profiles/utilizers`,
      {
        headers: { Authorization: newToken },
        params: { search: value },
      }
    );

    if (response instanceof Error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      return [];
    }

    const { data = [] } = response;
    return data;
  };

  const fetchAdminCondos = async (adminId, accountability) => {
    if (!adminId) {
      setCondos([]);
      return;
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/condos/invoices/${adminId}`,
      {
        params: {
          limit: 300,
          accountability,
        },
        headers: { Authorization: newToken },
      }
    );

    if (response instanceof Error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      return [];
    }

    const { data = [] } = response;

    setCondos(data.condos);
  };

  const fetchAdminCustomers = async (adminId) => {
    if (!adminId) {
      setCustomers([]);
      return;
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/customer-extra?profileId=${adminId}`,
      {
        params: {
          limit: 300,
        },
        headers: { Authorization: newToken },
      }
    );

    if (response instanceof Error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      return [];
    }

    const { data = [] } = response;
    console.log('Customers searched:', data);
    setCustomers(data);
  };

  const fetchUtilizer = async (adminId) => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/utilizers/${adminId}`,
      {
        headers: { Authorization: newToken },
      }
    );

    if (response instanceof Error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      return [];
    }

    const { data = {} } = response;
    console.log('Utilizer searched:', data);
    setUtilizer(data);
    return data;
  };

  const onNewInvoiceConfirm = async (data) => {
    if (data.admin === '' && data.estateAgent !== '') {
      data.admin = data.estateAgent;
    } else if (data.admin === '' && data.utilizer !== '') {
      data.admin = data.utilizer;
    } else if (data.admin === '' && data.commercial !== '') {
      data.admin = data.commercial;
    } else if (data.admin === '' && data.association !== '') {
      data.admin = data.association;
    }
    delete data.estateAgent;
    delete data.commercial;
    delete data.utilizer;
    delete data.association;
    showLoading();

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/invoices`,
      data,
      {
        headers: { Authorization: newToken },
      }
    );
    hideLoading();

    if (response instanceof Error) {
      const capsulateError = { error: response };
      const { error } = capsulateError;
      const { response: errorResponse, message: errorMessage } = error;
      const { data: errorData } = errorResponse || {};
      const { message: dataMessage } = errorData || {};
      alert.error(dataMessage || errorMessage || t('COMMON:GENERIC_ERROR'));
      return hideLoading();
    }

    alert.success(t('COMMON:SUCCESS_UPDATE'));
    fetchInvoices();
  };

  const onInvoiceUpdate = async (data) => {
    const { _id } = data;
    if (data.admin === '' && data.estateAgent !== '') {
      data.admin = data.estateAgent;
    } else if (data.admin === '' && data.utilizer !== '') {
      data.admin = data.utilizer;
    } else if (data.admin === '' && data.commercial !== '') {
      data.admin = data.commercial;
    }
    delete data.estateAgent;
    delete data.commercial;
    delete data.utilizer;
    delete data.invoiceIaf;
    delete data.commercialShare;
    delete data.earnedCommercialShare;
    delete data.billableCommercialShare;
    delete data.studioManager;
    delete data.overdueInvoicesCount;
    delete data.latestOverdueInvoiceDate;
    showLoading();

    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/invoices/${_id}`,
      data,
      {
        headers: { Authorization: newToken },
      }
    );
    hideLoading();

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    alert.success(t('COMMON:SUCCESS_UPDATE'));
    fetchInvoices();
  };

  const onAddPayment = async ({ invoiceId, data }) => {
    showLoading();

    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/invoices/${invoiceId}/payments`,
      data,
      {
        headers: { Authorization: newToken },
      }
    );
    hideLoading();

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    alert.success(t('COMMON:SUCCESS_UPDATE'));
    fetchInvoices();
  };

  const onDeletePayment = async ({ invoiceId, paymentIndex }) => {
    showLoading();

    const response = await axios.delete(
      `${process.env.REACT_APP_API_URL}/invoices/${invoiceId}/payments/${paymentIndex}`,
      {
        headers: { Authorization: newToken },
      }
    );
    hideLoading();

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    alert.success(t('COMMON:SUCCESS_UPDATE'));
    fetchInvoices();
  };

  const onValidateInvoice = async (invoiceId) => {
    showLoading();

    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/invoices/${invoiceId}/validate`,
      { validated: true },
      {
        headers: { Authorization: newToken },
      }
    );
    hideLoading();

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    alert.success(t('COMMON:SUCCESS_UPDATE'));
    fetchInvoices();
  };

  const onInvoiceDelete = async (invoiceId) => {
    showLoading();

    const response = await axios.delete(
      `${process.env.REACT_APP_API_URL}/invoices/${invoiceId}`,
      {
        headers: { Authorization: newToken },
      }
    );
    hideLoading();

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    alert.success(t('COMMON:SUCCESS_UPDATE'));
    fetchInvoices();
  };

  function onFilter(queryDiff) {
    if (queryDiff && typeof queryDiff.search === 'string') {
      localStorage.setItem('myInvoiceSearch', queryDiff.search);
    }
    if (
      (queryDiff.accountabilityYear &&
        queryDiff.accountabilityYear !== accountabilityYear) ||
      queryDiff.accountabilityYear === '' ||
      (queryDiff.toBill && queryDiff.toBill !== toBill) ||
      (toBill && queryDiff.toBill === '')
    ) {
      queryDiff.accountabilityMonth = '';
    }
    setQuery(queryDiff);
  }

  const [hasImportedInvoices, setHasImportedInvoices] = useState(false);
  const [invoicesImportFeedbackData, setInvoicesImportFeedbackData] = useState(
    {}
  );

  const toggleShowImportFeedback = () => setHasImportedInvoices((v) => !v);

  const onImportInvoices = async (file) => {
    showLoading();
    const { error, data } = await invoicesAPIs.importInvoices({
      newToken,
      file,
    });
    hideLoading();
    toggleShowImportFeedback();
    setInvoicesImportFeedbackData({
      ...data,
      validationErrors: error?.response?.data,
    });
    fetchInvoices();
    if (error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
    }
  };

  return (
    <InvoicesPresentational
      data={invoices}
      query={query}
      onFilter={onFilter}
      searchAdministrators={searchAdministrators}
      searchEstateAgents={searchEstateAgents}
      searchAssociations={searchAssociations}
      searchCommercials={searchCommercials}
      searchUtilizers={searchUtilizers}
      fetchAdminCondos={fetchAdminCondos}
      fetchAdminCustomers={fetchAdminCustomers}
      fetchUtilizer={fetchUtilizer}
      condos={condos}
      customersExtra={customers}
      utilizer={utilizer}
      onNewInvoiceConfirm={onNewInvoiceConfirm}
      onInvoiceUpdate={onInvoiceUpdate}
      onAddPayment={onAddPayment}
      onDeletePayment={onDeletePayment}
      onValidateInvoice={onValidateInvoice}
      onInvoiceDelete={onInvoiceDelete}
      onImportInvoices={onImportInvoices}
      toggleShowImportFeedback={toggleShowImportFeedback}
      invoicesImportFeedbackData={invoicesImportFeedbackData}
      hasImportedInvoices={hasImportedInvoices}
      profileType={profile.type}
    />
  );
}

export default Invoices;
